// Type imports:
import { IEventReport, IKeldanModel, OwnedReport } from "./KeldanTypes"

export interface ICompanyViewModel {
    id?: string,
    isAuthenticated?: boolean,
    isSubscriber?: boolean,
    accessToken?: string,
}

export interface IAuthenticationWithIdAndEventId {
    isAuthenticated?: boolean;
    isSubscriber?: boolean;
    id?: string;
    eventId?: string;
}

export interface ICompanyReportModel {
    id: string,
    eventId: string
};

export interface ReportsSearchModel extends IKeldanModel {
    existing?: OwnedReport[],
    dateTo: KeldanDate,
    files?: KeldanFile[],
    step: number | null,
    dateFrom: KeldanDate,
    papers?: KeldanPapers[],
    ssn: string | null
}

export interface CompanyInfo {
    CompanyId: string;
    Name: string;
    Ticker: string;
    Language: string;
    Industry: string;
    Description: string;
    Id: number;
    IsatId: number | null;
    IsatDescription: string;
}

export interface KeldanCompanyInfoStripped {
    CompanyId: number;
    CompanyName: string;
    CompanyAddress: string;
    CompanyPostalCode: number;
    CompanyCity: string;
    IsatId: number;
    IsatDescriptionIs: string;
    IsatDescriptionEn: string;
    ChairmanName: string;
    ExecutiveName: string;
    AuditorName: string;
    DateDeregistered: string | null;
    DateRegistered: string | null;
}

export interface ComparableCompany {
    atvinnugreinarnumer_isat: string;
    company_id: string;
    name: string;
    max_fiscal_year: number;
}

export interface KeldanDate {
    date: number,
    day?: number,
    hours?: number,
    minutes?: number,
    month: number,
    seconds?: number,
    time?: number,
    timezoneOffset?: number,
    year: number
}

export const getDate = (d: KeldanDate) => {
    return new Date(d.year + 1900, d.month, d.date, d.hours, d.minutes, d.seconds);
}

export interface KeldanFile {
    receiptId: string | null,
    ssn: string | null,
    type: string | null,
    year: number | null,
    returnform: string | null,
    returned: KeldanDate,
    accepted: KeldanDate
}

export interface KeldanPapers {
    fromDate: string | null,
    toDate: string | null,
    papers: KeldanPaper[],
    status: number | null,
    overviewType: string | null,
    error: string | null
}

export interface KeldanPaper {
    id: string | null,
    collected: KeldanDate,
    lastModified: KeldanDate,
    registered: KeldanDate,
    ssn: string | null,
    type: string | null
}

export interface ReportFullResult {
    EnName: string;
    IsName: string;
    KeyId: number | null;
    Level: number | null;
    ParentId: number | null;
    SortingId: number | null;
    PeriodEnd: string | null;
    KeyValueInverted: number ;
    PeriodStart: string | null;
    FiscalYear: number;
    Author: string;
    Currency: string;
    NumbersIn: number;
    ReportId: number | null;
    Name: string;
    Id: number | null;
    PeriodName: string;
    UseNumbersIn: boolean;
}

export interface CompanyReportInfo {
    FiscalYear: number,
    PeriodStart: string | null,
    PeriodEnd: string | null,
    CreatedTimestamp: string | null,
    EditedTimestamp: string | null,
    Currency: string | null,
    KeyId: number,
    KeyValue: number,
    Calc: boolean,
    Level: number | null,
    IsName: string | null,
    EnName: string | null,
    ParentId: number | null,
    Additive: boolean,
    UseNumbersIn: boolean,
    KeyPath: string | null,
    Formula: string | null,
    SortingId: number | null,
    SortingLevel: number | null,
    SaveValue: boolean,
    NumbersIn: number | null
}

export interface CompanyAssetsReport {
    reportBought: string,
    eventId: string,
    owned: number,
    sold: number
}

export interface IAPI1819 {
    kennitala?: string,
    logo?: string,
    link?: string,
    name?: string,
    phones?: string[],
    error?: string,
}

export interface ICompanyOwners {
    nationality: string;
    busetuLand: string;
    kt_fyrirtaekis: string;
    ktFyrirtaekis: string;
    companySSN: string;
    name: string;
    faedingarManudur: string;
    birthMonth: string;
    id: string;
    ownedPercentage: number;
    residence: string;
}

export interface AvailableReports {
    EditedTimestamp: string | null;
    CreatedTimestamp: string | null;
    CountEmployees: number | null;
    PeriodName: string;
    PeriodEnd: string | null;
    PeriodStart: string | null;
    FiscalYear: number | null;
    Currency: string;
    NumbersIn: number | null;
    ReportId: number | null;
    CompanyName: string;
    Id: number | null;
    Industry: number | null;
    Language: string;
    CompanyId: string;
    ForGroup: boolean;
    CountKeys: number | null;
}


export interface CompanyDetailedOverview {
    addresses: boolean;
    procuration: BoardMember[];
    managers: BoardMember[];
    substitutes: BoardMember[];
    directors: BoardMember[];
    isats: CompanyDetailedIsat[];
    boardChairman: BoardMember;
    company: CompanyDetailedDetails;
    accountants: BoardMember[];
    board: BoardMember[];
    founders: BoardMember[];
    members: AltBoardMember[];
    owners: CompanyOwner[];
}

export interface BoardMember {
    address: string;
    ssn: string;
    name: string;
    city: string;
    zip: string;
}

export interface AltBoardMember {
    address: string;
    ssn: string;
    name: string;
    zip: string;
    city: string;
    description: string;
    type: string;
    place: string;
}

export interface CompanyDetailedIsat {
    isat: string;
    description: string;
    description_en: string;
}

export interface CompanyDetailedDetails {
    acceptance: string;
    name: string;
    currency: string;
    capital: number;
    restrictions: string;
    ssn: string;
    duty: string;
    address: string;
    zip: string;
    city: string;
    form: string | null;
    formDescription: string;
    description: string;
    dateAccepted: number;
    lastChange: number;
    dateRejected: number;
    datePayed: number;
    lastChangeRSK: number;
    dateListed: number;
    lastBoardMeeting: number;
    dateRegistered: number;
    dateNotified: number;
}

export interface CompanyDetailedDate {
    timeInMillis: number;
    date: string;
}

export interface CompanyOwner {
    nationality: string;
    busetuLand: string;
    kt_fyrirtaekis: string;
    ktFyrirtaekis: string | null;
    companySSN: string;
    name: string;
    faedingarManudur: string;
    id: string;
    ownedPercentage: number;
}

export interface ComparableAvailability extends ComparableMeta {
    income_isk: boolean;
    expenses_isk: boolean;
    ebitda_isk: boolean;
    ebit_isk: boolean;
    profit_isk: boolean;
    assets_isk: boolean;
    equity_isk: boolean;
    liabilities_isk: boolean;
    cashfromoa_isk: boolean;
    cashfromia_isk: boolean;
    cashfromfa_isk: boolean;
}

export interface ComparableMeta extends ComparableIdentifier {
    numbers_in?: number;
    currency?: string;
    fiscal_year: number;
    count_employees?: number | null;
}

export interface ComparableIdentifier {
    name: string;
    company_id: string;
}


export interface CompareParameters {
    ssn: string;
    year: number;
    ssnList: string[];
}

export interface ComparableData extends ComparableMeta {
    income_isk?: number | null;
    expenses_isk?: number | null;
    ebitda_isk?: number | null;
    ebit_isk?: number | null;
    profit_isk?: number | null;
    assets_isk?: number | null;
    equity_isk?: number | null;
    liabilities_isk?: number | null;
    cashfromoa_isk?: number | null;
    cashfromia_isk?: number | null;
    cashfromfa_isk?: number | null;

    //For totals 
    income_delta?: number | null;
    income_delta_last?: number | null;
    expenses_delta_current?: number | null;
    expenses_delta_last?: number | null;
    roe_ratio?: number,
}


export interface ComparablesTotals {
    max_year: number;
    years: number[];
    income_count: number[];
    income_total: (number | null)[];
    income_me: (number | null)[];
    expenses_count: number[];
    expenses_total: (number | null)[];
    ebitda_count: number[];
    ebitda_total: (number | null)[];
    ebit_count: number[];
    ebit_total: (number | null)[];
    profit_count: number[];
    profit_total: (number | null)[];
    assets_count: number[];
    assets_total: (number | null)[];
    assets_me: (number | null)[];
    liabilities_count: number[];
    liabilities_total: (number | null)[];
    equity_count: number[];
    equity_total: (number | null)[];
    ebitda_ratio_count: number[];
    ebitda_ratio_ebitda_total: (number | null)[];
    ebitda_ratio_income_total: (number | null)[];
    equity_ratio_count: number[];
    equity_ratio_equity_total: (number | null)[];
    equity_ratio_assets_total: (number | null)[];
    roe_ratio_count: number[];
    roe_ratio_profit_total: (number | null)[];
    roe_ratio_equity_total: (number | null)[];
    income_delta_count: number[];
    income_delta_current: (number | null)[];
    income_delta_last: (number | null)[];
    expenses_delta_count: number[];
    expenses_delta_current: (number | null)[];
    expenses_delta_last: (number | null)[];
}

export interface ICompanyReportKey {
    additive: boolean | null
    formula: string | null
    id: number | null
    keyId: number | null
    keyName: string | null
    keyPath: string | null
    level: number | null
    parentId: number | null
    sortingId: number | null
    sortingLevel: number | null
    useNumbersIn: boolean | null
}

export interface ICompanyReportValue {
    author: string | null
    currency: string | null
    enName: string | null
    fiscalYear: number | null
    id: number | null
    isName: string | null
    keyId: number | null
    keyValueInverted: number | null
    level: number | null
    name: string | null
    numbersIn: number | null
    parentId: number | null
    periodEnd: string | null
    periodName: string | null
    periodStart: string | null
    reportId: number | null
    sortingId: number | null
    useNumbersIn: boolean | null
}

export interface ICompanyReportYear {
    editedTimestamp: string | null;
    createdTimestamp: string | null;
    countEmployees: number | null;
    periodName: string | null;
    periodEnd: string | null;
    periodStart: string | null;
    fiscalYear: number | null;
    currency: string | null;
    numbersIn: number | null;
    reportId: number | null;
    companyName: string;
    id: number | null;
    industry: number | null;
    language: string | null;
    companyId: string | null;
    forGroup: boolean | null;
    countKeys: number | null;
}

export interface IViewCompanyReport {
    keys: ICompanyReportKey[]
    mainData: ICompanyReportValue[]
    mainYear: ICompanyReportYear,
    secondaryData: ICompanyReportValue[] | null
    secondaryYear: ICompanyReportYear | null
}

export interface IViewCompanyCompare {
    compareParameters?: IEventReport<CompareParameters>,
    companyData?: ComparableData[],
    comparablesData: {[key: string]: ComparableData[]; },
    totals?: ComparablesTotals | null,
}

export interface ICompanyValuation {
    change_in_nwc: number | null,
    beta: number | null,
    company_valuation: number | null,
    free_cashflow: number | null,
    market_risk_premium: number | null,
    corporate_tax_rate: number | null,
    edited_timestamp: string | null,
    interest_coverage_rating: string | null,
    average_debt_ratio: number | null,
    average_ebit: number | null,
    depreciation: number | null,
    fiscal_year: number | null,
    equity_valuation: number | null,
    interest_coverage: number | null,
    company_id: string | null,
    interest_coverage_spread: number | null,
    period_start: string | null,
    release_date: string | null,
    interest_expense: number | null,
    currency: string | null,
    cash: number | null,
    industry: string | null,
    created_timestamp: string | null,
    interest_bearing_debt: number | null,
    symbol: string | null,
    beta_year: number | null,
    taxes: number | null,
    return_on_debt: number | null,
    riskfree_last_trade_date: string | null,
    investment: number | null,
    ebit: number | null,
    for_group: boolean | null,
    period_end: string | null,
    company_name: string | null,
    gdp_growth_rate: number | null,
    interest_coverage_ratio_to: number | null,
    riskfree_last_yield: number | null,
    return_on_equity: number | null,
    interest_coverage_ratio_from: number | null,
    average_equity_ratio: number | null,
    wacc: number | null,
    income: number | null,
    report_count: number | null
}